import React, { useEffect, useState } from 'react';
import {
  Empty,
  Modal,
  Popover,
  Spin,
  Table,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import CustomButton from '../../common/Button/Button';
import {
  getInvoicesAuditLogs,
  getPayoutsAuditLogs,
  getTransactionAuditLogs,
} from '../../../services/AuditLogAPI';
import colors from '../../../constants/ColourConstants';
import moment from 'moment';

const { Text } = Typography;

export default function AuditLogs({
  recordId,
  from,
  auditData,
  setTransactionsAuditData,
  setInvoicesAuditData,
  setPayoutAuditData,
  children,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMore, setViewMore] = useState(false);
  const [open, setOpen] = useState(false);

  const sort = (data) => {
    const sortData =
      data.length > 0
        ? data?.sort(
            (a, b) =>
              new Date(b?.recordCreatedDate) - new Date(a?.recordCreatedDate),
          )
        : [];
    return sortData;
  };

  const handleAuditLogs = async (newOpen) => {
    setOpen(newOpen);
    if (newOpen && !auditData) {
      setLoading(true);
      setError(null);
      try {
        if (from === 'transactions') {
          const data = await getTransactionAuditLogs(recordId);
          setTransactionsAuditData({
            ...auditData,
            [`${recordId}`]: sort(data),
          });
        }
        if (from === 'invoices') {
          const data = await getInvoicesAuditLogs(recordId);
          setInvoicesAuditData({ ...auditData, [`${recordId}`]: sort(data) });
        }
        if (from === 'payouts') {
          const data = await getPayoutsAuditLogs(recordId);
          setPayoutAuditData({ ...auditData, [`${recordId}`]: sort(data) });
        }
      } catch (err) {
        console.log(err);
        setError(err.message || 'Failed to load content');
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const tableBody = document.querySelector('.ant-table-body');

    const handleScroll = () => {
      setOpen(false);
    };

    tableBody?.addEventListener('scroll', handleScroll);

    return () => {
      tableBody?.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const items = () => {
    const _data = auditData?.slice(0, 5)?.map((item) => {
      const date = item.recordLastModified.split('T');
      const time = moment(date[1], 'HH:mm:ss.SSSSSS').format('hh:mm A');
      return {
        label: (
          <div>
            <Text strong>{date && date[0]}</Text>
            <br />
            <Text type="secondary">{`${time} (UTC)`}</Text>
          </div>
        ),
        children: (
          <div style={{ marginTop: 8 }}>
            <Text style={{ fontWeight: 400, fontSize: 14 }}>
              {item.fieldChanged}:
            </Text>
            <br />
            <Text>
              {' '}
              {`${item.oldValue || `" "`} -> ${item.newValue || `" "`}`}
            </Text>
          </div>
        ),
      };
    });
    return _data;
  };
  const content = () => {
    return (
      <>
        {loading ? (
          <Spin loading style={{ justifyContent: 'center', display: 'flex' }} />
        ) : error ? (
          <Text
            style={{
              justifyContent: 'center',
              display: 'flex',
              color: colors.colorError,
            }}
          >
            {error}
          </Text>
        ) : auditData?.length > 0 ? (
          <div style={{ width: '350px' }}>
            <Timeline mode={'left'} items={items()} />

            <div style={{ textAlign: 'center' }}>
              <CustomButton
                shape="round"
                size="small"
                onClick={() => {
                  setOpen(false), setViewMore(true);
                }}
              >
                {auditData?.length >= 5 ? `View Audit Logs` : `More details`}
              </CustomButton>
            </div>
          </div>
        ) : (
          <Empty description={'No modification data'} />
        )}
      </>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 155,
      render: (value) => {
        return (
          <Tooltip title={<>{value}</>}>
            <Text
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {value}
            </Text>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.id?.localeCompare(b.id),
    },
    {
      title: 'Field',
      dataIndex: 'fieldChanged',
      key: 'fieldChanged',
      width: 155,
      render: (value) => {
        return (
          <Tooltip title={value}>
            <Text
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {value}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Old',
      dataIndex: 'oldValue',
      key: 'oldValue',
      width: 155,
      render: (value) => {
        return (
          <Tooltip title={value}>
            <Text
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {value}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'New',
      dataIndex: 'newValue',
      key: 'newValue',
      width: 155,
      render: (value) => {
        return (
          <Tooltip title={value}>
            <Text
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {value}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Date & Time Modified',
      dataIndex: 'recordCreatedDate',
      key: 'recordCreatedDate',
      width: 195,
      render: (value) => {
        const date = value.split('T');
        const time = moment(date[1], 'HH:mm:ss.SSSSSS').format('hh:mm A');
        return (
          <Tooltip title={`${date[0]} | ${time} (UTC)`}>
            <Text
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {`${date[0]} | ${time} (UTC)`}
            </Text>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.recordCreatedDate?.localeCompare(b.recordCreatedDate),
    },
    {
      title: 'Modified  By',
      dataIndex: 'createdById',
      key: 'createdById',
      width: 155,
      render: (value) => {
        return (
          <Tooltip title={value}>
            <Text
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {value}
            </Text>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.createdById?.localeCompare(b.createdById),
    },
  ];
  return (
    <>
      <Popover
        placement="rightTop"
        title={'Modifications'}
        content={content()}
        trigger="click"
        open={open}
        onOpenChange={handleAuditLogs}
      >
        {children}
      </Popover>
      <Modal
        title={`Modifications of ${recordId}`}
        open={viewMore}
        onCancel={() => setViewMore(false)}
        width={'80%'}
        footer={[]}
      >
        <Table
          style={{ height: '100%' }}
          columns={columns}
          dataSource={auditData}
          pagination={false}
          bordered
          scroll={{
            y: 'calc(100vh - 350px)',
          }}
        />
      </Modal>
    </>
  );
}
